import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import sidebarLogo from '@/assets/sidebar-logo.png'
import { cn, avatarColor } from '@/lib/utils'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { queryClient } from '@/main'
import { LogOut } from 'lucide-react'
import { openCommandPalette } from '@/components/command/commandPaletteStore'
import { MAIN_NAV_ROUTES, ADMIN_NAV_ROUTES, SETTINGS_NAV_ROUTE, type NavRoute } from './navRoutes'

export function SaasSidebar() {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { sidebarOpen, setSidebarOpen } = useUIStore()
  const { logout, user } = useAuthStore()

  const handleLogout = () => {
    queryClient.clear()
    logout()
  }

  const initials = user?.username ? user.username.slice(0, 2).toUpperCase() : '??'
  const avatarBg = user?.username ? avatarColor(user.username) : 'bg-muted'

  // Cast t to accept any string key — navRoutes uses dynamic keys not in the strict union
  const tAny = t as (k: string) => string

  /** Resolve a route label — literal overrides i18n key. */
  const resolveLabel = (r: NavRoute) => r.labelLiteral ?? tAny(r.labelKey)

  const navItem = (r: NavRoute, onClickExtra?: () => void) => {
    const label = resolveLabel(r)
    const isActive = location.pathname === r.href
    return (
      <Link
        key={r.href}
        to={r.href}
        onClick={() => { setSidebarOpen(false); onClickExtra?.() }}
        className={cn(
          'h-7 px-2.5 mx-2 rounded-md flex items-center gap-2.5 text-[13px] transition-colors duration-100',
          isActive
            ? 'bg-muted text-foreground font-medium'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        )}
      >
        <r.Icon className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </Link>
    )
  }

  return (
    <>
      {/* Desktop: fixed flat sidebar w-60 */}
      <aside
        data-testid="saas-sidebar"
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-60 bg-background border-r border-border flex flex-col',
          'hidden lg:flex'
        )}
      >
        {/* Logo — natural small size, no collapse toggle */}
        <Link
          to="/"
          className="flex h-14 items-center px-4 border-b border-border flex-shrink-0"
        >
          <img
            src={sidebarLogo}
            alt="DeterCamAI"
            className="h-7 w-auto max-w-full object-contain block"
          />
        </Link>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0.5">

          {/* Main nav */}
          <div className="flex flex-col gap-0.5">
            {MAIN_NAV_ROUTES.map(r => navItem(r))}
          </div>

          {/* Admin section */}
          <div className="flex flex-col gap-0.5">
            <p className="px-4 pt-4 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('sidebar.admin')}
            </p>
            {ADMIN_NAV_ROUTES.map(r => user?.is_superuser ? navItem(r) : null)}
            {navItem(SETTINGS_NAV_ROUTE)}
          </div>
        </nav>

        {/* Footer: user row + ⌘K chip + logout icon */}
        <div className="border-t border-border p-3 flex-shrink-0">
          {user && (
            <div className="flex items-center gap-2">
              <div className={cn(
                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-[10px] font-bold text-white',
                avatarBg
              )}>
                {initials}
              </div>
              <span className="truncate flex-1 text-[13px] text-foreground font-medium min-w-0">
                {user.username}
              </span>
              {/* ⌘K hint chip */}
              <button
                onClick={() => openCommandPalette()}
                className="ml-auto border border-border rounded px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors duration-100 flex-shrink-0"
                aria-label="Open command palette"
              >
                ⌘K
              </button>
              {/* Logout icon */}
              <button
                onClick={handleLogout}
                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-100 flex-shrink-0"
                title={t('sidebar.logout')}
              >
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile: slide-in panel w-64 (labels visible, same contract as CommandSidebar mobile) */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-[100dvh] w-64 bg-background border-r border-border flex flex-col',
          'lg:hidden',
          !sidebarOpen && '-translate-x-full',
          'transition-transform duration-300'
        )}
      >
        {/* Logo */}
        <div className="flex h-14 items-center border-b border-border px-4 flex-shrink-0">
          <Link to="/" onClick={() => setSidebarOpen(false)}>
            <img
              src={sidebarLogo}
              alt="DeterCamAI"
              className="h-7 w-auto object-contain block"
            />
          </Link>
        </div>

        {/* Mobile nav */}
        <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0.5">
          <div className="flex flex-col gap-0.5">
            {MAIN_NAV_ROUTES.map(r => navItem(r))}
          </div>
          <div className="flex flex-col gap-0.5">
            <p className="px-4 pt-4 pb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
              {t('sidebar.admin')}
            </p>
            {ADMIN_NAV_ROUTES.map(r => user?.is_superuser ? navItem(r) : null)}
            {navItem(SETTINGS_NAV_ROUTE)}
          </div>
        </nav>

        {/* Mobile footer: avatar + username + logout */}
        <div className="border-t border-border p-3 flex items-center gap-2 flex-shrink-0">
          {user && (
            <div className={cn(
              'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-[10px] font-bold text-white',
              avatarBg
            )}>
              {initials}
            </div>
          )}
          {user && (
            <span className="truncate flex-1 text-[13px] text-foreground font-medium min-w-0">
              {user.username}
            </span>
          )}
          <button
            onClick={handleLogout}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors duration-100 flex-shrink-0"
            title={t('sidebar.logout')}
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </aside>
    </>
  )
}
